import React, { useMemo, useState } from "react"
import dayjs from "dayjs"
import type { DunningUserMemo } from "../types"
import { useUserMemos } from "../hooks/useUserMemos"
import { Panel } from "./shared"

const emptyForm = { title: "", body: "" }

/** メモ1件。操作（編集・削除）を渡さなければ読み取り専用（AIドラフト検証の架空ケース表示でも使う） */
export const MemoItem: React.FC<{
  memo: Pick<DunningUserMemo, "title" | "body" | "authorName" | "updatedAt">
  actions?: React.ReactNode
}> = ({ memo, actions }) => (
  <li className="rounded-lg border border-gray-200 bg-white px-4 py-3">
    <div className="flex items-start justify-between gap-3">
      <div className="min-w-0">
        {memo.title && <p className="text-[1.3rem] font-semibold text-black">{memo.title}</p>}
        <p className="mt-1 whitespace-pre-wrap text-[1.3rem] text-black">{memo.body}</p>
      </div>
      {actions && <div className="flex shrink-0 gap-3">{actions}</div>}
    </div>
    <p className="mt-2 text-[1.1rem] text-black">
      記入者: {memo.authorName ?? "不明"} / {dayjs(memo.updatedAt).format("YYYY年M月D日 HH:mm")}
    </p>
  </li>
)

export const UserMemosPanel: React.FC<{ caseId: number }> = ({ caseId }) => {
  const {
    memos,
    isLoading,
    error,
    saveMemo,
    isSaving,
    saveError,
    deleteMemo,
    deletingId,
    deleteError,
  } = useUserMemos(caseId)
  const [editingId, setEditingId] = useState<number | undefined>()
  const [form, setForm] = useState(emptyForm)
  const [saved, setSaved] = useState(false)
  const orderedMemos = useMemo(
    () =>
      [...memos].sort((left, right) => {
        const createdAtDifference =
          dayjs(left.createdAt).valueOf() - dayjs(right.createdAt).valueOf()
        return createdAtDifference !== 0 ? createdAtDifference : left.id - right.id
      }),
    [memos]
  )

  const startEdit = (memo: DunningUserMemo) => {
    setEditingId(memo.id)
    setForm({ title: memo.title ?? "", body: memo.body })
    setSaved(false)
  }

  const resetForm = () => {
    setEditingId(undefined)
    setForm(emptyForm)
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!form.body.trim() || isSaving) return

    setSaved(false)
    const ok = await saveMemo({
      id: editingId,
      title: form.title.trim(),
      body: form.body.trim(),
    })
    if (!ok) return

    resetForm()
    setSaved(true)
  }

  const handleDelete = async (memo: DunningUserMemo) => {
    if (!window.confirm("このメモを削除しますか？")) return

    const deleted = await deleteMemo(memo.id)
    if (deleted && editingId === memo.id) resetForm()
  }

  return (
    <Panel title="メモ欄（チャットページと共通）">
      <p className="mb-3 text-[1.1rem] text-black">
        チャット画面と督促画面のどちらから編集しても、同じユーザーメモに反映されます。
      </p>

      <form onSubmit={submit} className="rounded-lg border border-gray-200 bg-gray-50 p-4">
        <input
          type="text"
          value={form.title}
          maxLength={64}
          disabled={isSaving}
          onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
          placeholder="メモのタイトル（任意）"
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-[1.25rem] text-black"
        />
        <textarea
          rows={3}
          value={form.body}
          maxLength={2048}
          disabled={isSaving}
          onChange={(event) => setForm((current) => ({ ...current, body: event.target.value }))}
          placeholder="メモの本文"
          className="mt-2 w-full rounded-lg border border-gray-300 bg-white p-3 text-[1.3rem] text-black"
        />
        <div className="mt-2 flex flex-wrap items-center justify-end gap-3">
          {saveError && <span className="mr-auto text-[1.15rem] text-red-600">{saveError}</span>}
          {saved && !saveError && (
            <span className="mr-auto text-[1.15rem] font-medium text-green-600">保存しました</span>
          )}
          {editingId != null && (
            <button
              type="button"
              disabled={isSaving}
              onClick={resetForm}
              className="text-[1.2rem] text-black underline disabled:opacity-50"
            >
              キャンセル
            </button>
          )}
          <button
            type="submit"
            disabled={!form.body.trim() || isSaving}
            className="rounded-lg bg-primary px-4 py-2 text-[1.2rem] font-semibold text-white hover:opacity-90 disabled:opacity-50"
          >
            {isSaving ? "保存中…" : editingId == null ? "メモを追加" : "変更を保存"}
          </button>
        </div>
      </form>

      {error ? (
        <p className="mt-4 text-[1.3rem] text-red-600">メモの取得に失敗しました。</p>
      ) : isLoading ? (
        <p className="mt-4 text-[1.3rem] text-black">読み込み中…</p>
      ) : orderedMemos.length === 0 ? (
        <p className="mt-4 text-[1.3rem] text-black">メモはまだありません。</p>
      ) : (
        <>
          {deleteError && <p className="mt-4 text-[1.15rem] text-red-600">{deleteError}</p>}
          <ul className="mt-4 flex flex-col gap-3">
            {orderedMemos.map((memo) => {
              const isDeleting = deletingId === memo.id
              return (
                <MemoItem
                  key={memo.id}
                  memo={memo}
                  actions={
                    <>
                      <button
                        type="button"
                        disabled={isSaving || isDeleting}
                        onClick={() => startEdit(memo)}
                        className="text-[1.15rem] text-primary underline disabled:opacity-50"
                      >
                        編集
                      </button>
                      <button
                        type="button"
                        disabled={isSaving || isDeleting}
                        onClick={() => handleDelete(memo)}
                        className="text-[1.15rem] text-red-600 underline disabled:opacity-50"
                      >
                        {isDeleting ? "削除中…" : "削除"}
                      </button>
                    </>
                  }
                />
              )
            })}
          </ul>
        </>
      )}
    </Panel>
  )
}
